import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react"
import { createPortal } from "react-dom"

import { Button } from "@/components/ui/button"
import {
  clampIsoRangeToMaxDays,
  formatCalendarDate,
  formatDisplayDate,
  labelFromIsoRange,
} from "@/lib/dates"
import { computePresetRange } from "@/pages/dashboard/lib/presets"

/**
 * Hand-rolled month-grid calendar for the dashboard's custom date
 * range button. Parity: `dashboard.html:1975-2087` (render +
 * selectDate + applyCustomRange).
 *
 * Behaviour:
 *   - Click first day → sets start.
 *   - Click second day → sets end. If end < start, swap.
 *   - When both are set, auto-applies after 100ms.
 *   - Apply button commits the range and closes the popover.
 *   - Cancel just closes.
 *   - Quick presets: Last 7 days / This month / Last month.
 *   - `‹` `‹‹` `›` `››` navigation arrows for month / year.
 *   - Today is marked with a border; selected start/end and in-range
 *     days use solid tints.
 *
 * The popup is portaled to `document.body` and fixed under the trigger
 * so fixed-viewport pages (`overflow: hidden`) do not clip it.
 */

const PANEL_WIDTH_PX = 324
const PANEL_VIEWPORT_PAD_PX = 8

const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"] as const
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const

const QUICK_PRESETS = [
  { key: "last7", label: "Last 7 days" },
  { key: "thismonth", label: "This month" },
  { key: "last30", label: "Last month" },
] as const

type QuickPresetKey = (typeof QUICK_PRESETS)[number]["key"]

interface DateRangePickerProps {
  open: boolean
  onClose: () => void
  /** Current committed start date (YYYY-MM-DD) or null if none. */
  initialStart: string | null
  initialEnd: string | null
  onApply: (startIso: string, endIso: string, displayLabel: string) => void
  /**
   * When set, outside-click dismissal treats clicks inside this element as
   * inside the picker (e.g. wrap the trigger button + panel). Otherwise only
   * the panel node is used, and the trigger is incorrectly treated as outside.
   */
  outsideBoundaryRef?: RefObject<HTMLElement | null>
  /**
   * Optional preset resolver (e.g. Ads Manager reporting timezone).
   * Defaults to the shared local-calendar dashboard presets.
   */
  resolveQuickPreset?: (key: QuickPresetKey) => {
    startDate: string
    endDate: string
    displayLabel: string
  }
  /**
   * Inclusive max span in days (e.g. 30). Any start date is allowed; end is
   * clamped so the window never exceeds this length.
   */
  maxRangeDays?: number
}

export function DateRangePicker({
  open,
  onClose,
  initialStart,
  initialEnd,
  onApply,
  outsideBoundaryRef,
  resolveQuickPreset,
  maxRangeDays,
}: DateRangePickerProps) {
  const [calendarStart, setCalendarStart] = useState<Date | null>(null)
  const [calendarEnd, setCalendarEnd] = useState<Date | null>(null)
  const [viewMonth, setViewMonth] = useState<number>(() =>
    new Date().getMonth(),
  )
  const [viewYear, setViewYear] = useState<number>(() =>
    new Date().getFullYear(),
  )

  // Ref kept so the auto-apply setTimeout can read the latest state
  // without a stale closure.
  const pendingApplyRef = useRef<number | null>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [panelPos, setPanelPos] = useState<{ top: number; left: number } | null>(
    null,
  )

  // On open (or when the committed range changes while open): seed
  // calendar state from the committed range. Adjusted during render
  // instead of in an effect so the seeded grid shows on the first paint.
  const [seededFrom, setSeededFrom] = useState<{
    open: boolean
    initialStart: string | null
    initialEnd: string | null
  }>({ open: false, initialStart: null, initialEnd: null })
  if (
    seededFrom.open !== open ||
    seededFrom.initialStart !== initialStart ||
    seededFrom.initialEnd !== initialEnd
  ) {
    setSeededFrom({ open, initialStart, initialEnd })
    if (open && initialStart && initialEnd) {
      const s = new Date(initialStart + "T00:00:00")
      const e = new Date(initialEnd + "T00:00:00")
      setCalendarStart(s)
      setCalendarEnd(e)
      setViewMonth(s.getMonth())
      setViewYear(s.getFullYear())
    } else if (open) {
      setCalendarStart(null)
      setCalendarEnd(null)
      const now = new Date()
      setViewMonth(now.getMonth())
      setViewYear(now.getFullYear())
    }
  }

  // Clean up any pending auto-apply timer on unmount or close.
  useEffect(() => {
    if (!open && pendingApplyRef.current !== null) {
      window.clearTimeout(pendingApplyRef.current)
      pendingApplyRef.current = null
    }
  }, [open])

  useLayoutEffect(() => {
    if (!open) {
      setPanelPos(null)
      return
    }
    const place = () => {
      const anchor = outsideBoundaryRef?.current
      if (!anchor) return
      const rect = anchor.getBoundingClientRect()
      const left = Math.min(
        Math.max(PANEL_VIEWPORT_PAD_PX, rect.right - PANEL_WIDTH_PX),
        window.innerWidth - PANEL_WIDTH_PX - PANEL_VIEWPORT_PAD_PX,
      )
      setPanelPos({ top: rect.bottom + 6, left })
    }
    place()
    window.addEventListener("scroll", place, true)
    window.addEventListener("resize", place)
    return () => {
      window.removeEventListener("scroll", place, true)
      window.removeEventListener("resize", place)
    }
  }, [open, outsideBoundaryRef])

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (outsideBoundaryRef?.current?.contains(target)) return
      if (panelRef.current?.contains(target)) return
      onClose()
    }
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("mousedown", handleClickOutside)
    document.addEventListener("keydown", handleKey)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("keydown", handleKey)
    }
  }, [open, onClose, outsideBoundaryRef])

  const commitRange = useCallback(
    (start: Date, end: Date) => {
      const s = start <= end ? start : end
      const e = start <= end ? end : start
      let startIso = formatCalendarDate(s)
      let endIso = formatCalendarDate(e)
      if (maxRangeDays != null && maxRangeDays > 0) {
        const clamped = clampIsoRangeToMaxDays(startIso, endIso, maxRangeDays)
        startIso = clamped.startDate
        endIso = clamped.endDate
      }
      onApply(startIso, endIso, labelFromIsoRange(startIso, endIso))
    },
    [onApply, maxRangeDays],
  )

  const handleApplyClick = useCallback(() => {
    if (!calendarStart) return
    const endDate = calendarEnd ?? calendarStart
    commitRange(calendarStart, endDate)
  }, [calendarStart, calendarEnd, commitRange])

  const handleQuickPreset = useCallback(
    (key: QuickPresetKey) => {
      if (pendingApplyRef.current !== null) {
        window.clearTimeout(pendingApplyRef.current)
        pendingApplyRef.current = null
      }
      const range = resolveQuickPreset?.(key) ?? computePresetRange(key)
      let startDate = range.startDate
      let endDate = range.endDate
      if (maxRangeDays != null && maxRangeDays > 0) {
        const clamped = clampIsoRangeToMaxDays(startDate, endDate, maxRangeDays)
        startDate = clamped.startDate
        endDate = clamped.endDate
      }
      const s = new Date(startDate + "T00:00:00")
      const e = new Date(endDate + "T00:00:00")
      setCalendarStart(s)
      setCalendarEnd(e)
      setViewMonth(s.getMonth())
      setViewYear(s.getFullYear())
      onApply(startDate, endDate, labelFromIsoRange(startDate, endDate))
    },
    [onApply, resolveQuickPreset, maxRangeDays],
  )

  const handleDayClick = useCallback(
    (date: Date) => {
      const hasBoth = calendarStart !== null && calendarEnd !== null
      if (
        !calendarStart ||
        hasBoth ||
        (calendarStart && date < calendarStart)
      ) {
        setCalendarStart(date)
        setCalendarEnd(null)
        return
      }
      // Second click → set end, auto-apply after 100ms.
      setCalendarEnd(date)
      if (pendingApplyRef.current !== null) {
        window.clearTimeout(pendingApplyRef.current)
      }
      pendingApplyRef.current = window.setTimeout(() => {
        commitRange(calendarStart, date)
        pendingApplyRef.current = null
      }, 100)
    },
    [calendarStart, calendarEnd, commitRange],
  )

  const navMonth = useCallback((delta: number) => {
    setViewMonth((prev) => {
      const next = prev + delta
      if (next < 0) {
        setViewYear((y) => y - 1)
        return 11
      }
      if (next > 11) {
        setViewYear((y) => y + 1)
        return 0
      }
      return next
    })
  }, [])

  const navYear = useCallback((delta: number) => {
    setViewYear((y) => y + delta)
  }, [])

  // Build the 42-cell grid. Six rows starting Monday (ISO week), with
  // bleed into the previous / next month.
  const cells = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1)
    const lastDay = new Date(viewYear, viewMonth + 1, 0)
    const daysInMonth = lastDay.getDate()
    // getDay(): Sun=0 … Sat=6 → Monday-first index: Mon=0 … Sun=6
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7
    const prevMonth = viewMonth === 0 ? 11 : viewMonth - 1
    const prevYear = viewMonth === 0 ? viewYear - 1 : viewYear
    const prevMonthLastDay = new Date(prevYear, prevMonth + 1, 0).getDate()
    const nextMonth = viewMonth === 11 ? 0 : viewMonth + 1
    const nextYear = viewMonth === 11 ? viewYear + 1 : viewYear

    const list: Array<{ date: Date; day: number; otherMonth: boolean }> = []
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i
      list.push({
        date: new Date(prevYear, prevMonth, day),
        day,
        otherMonth: true,
      })
    }
    for (let day = 1; day <= daysInMonth; day++) {
      list.push({
        date: new Date(viewYear, viewMonth, day),
        day,
        otherMonth: false,
      })
    }
    const tail = 42 - list.length
    for (let day = 1; day <= tail; day++) {
      list.push({
        date: new Date(nextYear, nextMonth, day),
        day,
        otherMonth: true,
      })
    }
    return list
  }, [viewMonth, viewYear])

  const todayStr = useMemo(() => formatCalendarDate(new Date()), [])

  if (!open || !panelPos || typeof document === "undefined") return null

  return createPortal(
    <div
      ref={panelRef}
      className="fixed z-[80] w-[324px] rounded-lg border border-[#4a4a4a] bg-[#2a2a2a] p-3 shadow-lg"
      style={{ top: panelPos.top, left: panelPos.left }}
      role="dialog"
      aria-label="Select date range"
    >
      <div className="mb-2">
        <input
          type="text"
          readOnly
          value={
            calendarStart && calendarEnd
              ? formatCalendarDate(calendarStart) ===
                formatCalendarDate(calendarEnd)
                ? formatDisplayDate(calendarStart)
                : `${formatDisplayDate(calendarStart)} ~ ${formatDisplayDate(calendarEnd)}`
              : calendarStart
                ? formatDisplayDate(calendarStart)
                : ""
          }
          placeholder="Select date range"
          className="w-full rounded bg-[#1e1e1e] px-2 py-1 text-xs text-[#e0e0e0]"
        />
      </div>

      <div className="mb-2 grid grid-cols-3 gap-1">
        {QUICK_PRESETS.map((preset) => (
          <button
            key={preset.key}
            type="button"
            onClick={() => handleQuickPreset(preset.key)}
            className="w-full rounded border border-[#555] bg-[#3a3a3a] px-1 py-1 text-center text-[11px] font-medium leading-tight text-[#e0e0e0] hover:bg-[#4a4a4a]"
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div className="mb-1.5 flex items-center justify-between">
        <div className="text-xs font-semibold text-[#e0e0e0]">
          {MONTH_NAMES[viewMonth]} {viewYear}
        </div>
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            className="rounded bg-[#3a3a3a] px-1.5 py-0.5 text-xs text-[#e0e0e0] hover:bg-[#4a4a4a]"
            onClick={() => navMonth(-1)}
            aria-label="Previous month"
          >
            &lsaquo;
          </button>
          <button
            type="button"
            className="rounded bg-[#3a3a3a] px-1.5 py-0.5 text-xs text-[#e0e0e0] hover:bg-[#4a4a4a]"
            onClick={() => navYear(-1)}
            aria-label="Previous year"
          >
            &laquo;
          </button>
          <button
            type="button"
            className="rounded bg-[#3a3a3a] px-1.5 py-0.5 text-xs text-[#e0e0e0] hover:bg-[#4a4a4a]"
            onClick={() => navMonth(1)}
            aria-label="Next month"
          >
            &rsaquo;
          </button>
          <button
            type="button"
            className="rounded bg-[#3a3a3a] px-1.5 py-0.5 text-xs text-[#e0e0e0] hover:bg-[#4a4a4a]"
            onClick={() => navYear(1)}
            aria-label="Next year"
          >
            &raquo;
          </button>
        </div>
      </div>

      <div className="mb-0.5 grid grid-cols-7 gap-0.5 text-center text-[10px] text-[#888]">
        {WEEKDAYS.map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map(({ date, day, otherMonth }, idx) => {
          const iso = formatCalendarDate(date)
          const isToday = iso === todayStr
          let tint = ""
          if (calendarStart && calendarEnd) {
            const t = date.getTime()
            const s = calendarStart.getTime()
            const e = calendarEnd.getTime()
            if (t === s || t === e) {
              tint = "bg-[#667eea] text-white"
            } else if (t > s && t < e) {
              tint = "bg-[#3b4485] text-white"
            }
          } else if (
            calendarStart &&
            date.getTime() === calendarStart.getTime()
          ) {
            tint = "bg-[#667eea] text-white"
          }
          const base =
            "h-7 w-full rounded text-xs transition-colors hover:bg-[#4a4a4a]"
          const faded = otherMonth ? " text-[#666]" : " text-[#e0e0e0]"
          const border = isToday ? " border border-[#8b9aff]" : ""
          return (
            <button
              type="button"
              key={idx}
              className={`${base}${faded}${border} ${tint}`}
              onClick={() => handleDayClick(date)}
            >
              {day}
            </button>
          )
        })}
      </div>

      <div className="mt-2 flex justify-end gap-1.5">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={onClose}
          className="h-8 bg-[#3a3a3a] px-3 py-0 text-sm text-[#e0e0e0] hover:bg-[#4a4a4a]"
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={handleApplyClick}
          className="h-8 bg-[#667eea] px-3 py-0 text-sm text-white hover:bg-[#5568d3]"
          disabled={!calendarStart}
        >
          Apply
        </Button>
      </div>
    </div>,
    document.body,
  )
}
